/**
 * Page de configuration de l'activité
 * 
 * @component
 * @returns {JSX.Element} Composant ActivitySettings
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../../../hooks/useLanguage';
import { activitySettings as frActivitySettings } from '../../../../locales/fr/activitySettings';
import { activitySettings as arActivitySettings } from '../../../../locales/ar/activitySettings';
import { activitySettings as esActivitySettings } from '../../../../locales/es/activitySettings';
import useMyAppStore from '../../../../stores/myAppStore';
import ActivitySettingsService from '../../../../services/AppDataServices/services/ActivitySettings.service';

const translations = {
  fr: frActivitySettings,
  ar: arActivitySettings,
  es: esActivitySettings
};

const ActivitySettings = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { setPageTitle, setActivitySettings } = useMyAppStore();
  const [settings, setSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('business');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  React.useEffect(() => {
    setPageTitle(t.title);
  }, [lang, t.title, setPageTitle]);

  // Charger les settings au montage
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await ActivitySettingsService.getActivitySettings();
        // console.log('📦 ActivitySettings:', data);
        setSettings(data);
      } catch (error) {
        console.error('❌ Erreur lors du chargement:', error);
        setMessage('Erreur lors du chargement des paramètres');
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!settings) return;

    setIsLoading(true);
    setMessage('');

    try {
      console.log('🔄 Sauvegarde des paramètres...');
      const result = await ActivitySettingsService.updateActivitySettings(settings);
      setSettings(result);
      setActivitySettings(result); // MAJ du store global
      setMessage('Paramètres sauvegardés avec succès !');
      console.log('✅ Paramètres sauvegardés:', result);
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      setMessage('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleInputChange('businessInfo', 'logo', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!settings) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">{t.title}</h1>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2">Chargement des paramètres...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">{t.title}</h1>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Onglets */}
        <div className="tabs tabs-boxed bg-base-200 p-2">
          <button
            type="button"
            className={`tab ${activeTab === 'business' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('business')}
          >
            Informations du commerce
          </button>
          <button
            type="button"
            className={`tab ${activeTab === 'general' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            Paramètres généraux
          </button>
          <button
            type="button"
            className={`tab ${activeTab === 'tickets' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            Numérotation des tickets
          </button>
        </div>

        {/* Contenu des onglets */}
        <div className="bg-base-100 rounded-lg shadow p-6">
          
          {/* Onglet Informations du commerce */}
          {activeTab === 'business' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Informations du commerce</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    <span className="label-text">Identifiant du commerce</span>
                  </label>
                  <input
                    type="text"
                    value={settings.businessInfo?.businessId || ''}
                    onChange={(e) => handleInputChange('businessInfo', 'businessId', e.target.value)}
                    className="input input-bordered w-full"
                    required
                  />
                </div>
                
                <div>
                  <label className="label">
                    <span className="label-text">Nom du commerce</span>
                  </label>
                  <input
                    type="text"
                    value={settings.businessInfo?.businessName || ''}
                    onChange={(e) => handleInputChange('businessInfo', 'businessName', e.target.value)}
                    className="input input-bordered w-full"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Logo</span>
                </label>
                <div className="flex items-center space-x-4">
                  {settings.businessInfo?.logo && (
                    <img
                      src={settings.businessInfo.logo}
                      alt="Logo du commerce"
                      className="w-24 h-24 object-contain border rounded"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="file-input file-input-bordered w-full max-w-xs"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Onglet Paramètres généraux */}
          {activeTab === 'general' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Paramètres généraux</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    <span className="label-text">Devise</span>
                  </label>
                  <select
                    value={settings.generalSettings?.currency || 'EUR'}
                    onChange={(e) => handleInputChange('generalSettings', 'currency', e.target.value)}
                    className="select select-bordered w-full"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="Dh">Dh (د.م.)</option>
                  </select>
                </div>
                
                <div>
                  <label className="label">
                    <span className="label-text">Langue par défaut</span>
                  </label>
                  <select
                    value={settings.generalSettings?.defaultLanguage || 'fr'}
                    onChange={(e) => handleInputChange('generalSettings', 'defaultLanguage', e.target.value)}
                    className="select select-bordered w-full"
                  >
                    <option value="fr">Français</option>
                    <option value="ar">العربية</option>
                    <option value="es">Español</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Onglet Numérotation des tickets */}
          {activeTab === 'tickets' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Numérotation des tickets</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    <span className="label-text">Type de numérotation</span>
                  </label>
                  <select
                    value={settings.ticketNumbering?.type || 'yearly'}
                    onChange={(e) => handleInputChange('ticketNumbering', 'type', e.target.value)}
                    className="select select-bordered w-full"
                  >
                    <option value="monthly">Mensuel</option>
                    <option value="yearly">Annuel</option>
                    <option value="continuous">Continu</option>
                  </select>
                </div>
                
                <div>
                  <label className="label">
                    <span className="label-text">Préfixe</span>
                  </label>
                  <input
                    type="text"
                    value={settings.ticketNumbering?.prefix || ''}
                    onChange={(e) => handleInputChange('ticketNumbering', 'prefix', e.target.value)}
                    className="input input-bordered w-full"
                    placeholder="Ex: EPIC"
                  />
                </div>
                
                <div>
                  <label className="label">
                    <span className="label-text">Suffixe</span>
                  </label>
                  <input
                    type="text"
                    value={settings.ticketNumbering?.suffix || ''}
                    onChange={(e) => handleInputChange('ticketNumbering', 'suffix', e.target.value)}
                    className="input input-bordered w-full"
                    placeholder="Ex: -2024"
                  />
                </div>
                
                <div>
                  <label className="label">
                    <span className="label-text">Numéro de départ</span>
                  </label>
                  <input
                    type="number"
                    value={settings.ticketNumbering?.startNumber || 1}
                    onChange={(e) => handleInputChange('ticketNumbering', 'startNumber', parseInt(e.target.value))}
                    className="input input-bordered w-full"
                    min="1"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bouton de sauvegarde */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sauvegarde...
              </>
            ) : (
              'Sauvegarder les paramètres'
            )}
          </button>
        </div>

        {/* Message de statut */}
        {message && (
          <div className={`alert ${message.includes('succès') ? 'alert-success' : 'alert-error'}`}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

export default ActivitySettings; 